<template>
  <div>
    <ToggleTip
      ref="toggleTipProfile"
      position="right"
      :width="toggletipWidth"
    >
      <p
        class="medium margin-bottom-xxs"
        data-testid="usernickname-text"
      >
        {{ userNickname }}
      </p>
      <template v-if="isSyndicated">
        <p
          class="small"
          data-testid="syndicated-text"
        >
          This profile can’t be viewed since the reviewer did not purchase the item at {{ brandName }}.
        </p>
      </template>
      <template v-else>
        <template v-if="hasValidAuthorId">
          <template v-if="!isLoading">
            <div class="small margin-bottom-xxs">
              <div v-if="reviewerProfileData.totalAuthorReviews">
                <p
                  v-if="isEmployeeAuthor"
                  data-testid="brand-name-employee"
                >
                  {{ `${brandName} employee` }}
                </p>
                <p>{{ reviewerProfileData.totalAuthorReviewsText }} {{ reviewerProfileData.totalHelpfulVotesText }}</p>
              </div>
              <div v-else>
                <p
                  class="show-for-sr"
                  aria-live="polite"
                  data-testid="error-message-sr"
                >
                  Error: {{ errorMessage }}
                </p>
                <p
                  class="small"
                  aria-hidden="true"
                  data-testid="error-message"
                >
                  {{ errorMessage }}
                </p>
              </div>
            </div>
            <button
              v-if="reviewerProfileData.totalAuthorReviews"
              class="link-sm"
              data-testid="user-profile-button"
              @click="openReviewerModal"
            >
              See user profile
            </button>
          </template>
          <template v-else>
            <p
              class="small margin-top-xs margin-bottom-xs"
              :class="{ 'loading-container': isEmployeeAuthor }"
              data-testid="loading-message"
            >
              Loading...
            </p>
          </template>
        </template>
        <template v-else>
          <p
            class="show-for-sr"
            aria-live="polite"
            data-testid="error-message-sr"
          >
            Error: {{ errorMessage }}
          </p>
          <p
            class="small"
            aria-hidden="true"
            data-testid="error-message"
          >
            {{ errorMessage }}
          </p>
        </template>
      </template>
    </ToggleTip>
    <button
      class="link-sm"
      data-testid="usernickname-button"
      @click="handleUsernameClick"
    >
      {{ userNickname }}
    </button>
    <template v-if="reviewerProfileData?.authorReviews">
      <ReviewerModal
        :id="PROFILE_MODAL_ID"
        :reviews="reviewerProfileData?.authorReviews"
        :total-author-reviews="reviewerProfileData?.totalAuthorReviews"
        :is-loading="false"
        :author-id="authorId"
        :title="`${userNickname} (${reviewerProfileData.totalAuthorReviewsText})`"
        :page="page"
        @page-change="fetchProfileData"
      />
    </template>
  </div>
</template>

<script setup>
import {
  ref, computed, watch, onMounted,
} from 'vue';
import ToggleTip from '@atomic-ui/toggletip';
import { trackEvent } from '@utils/bvPixel';
import { useDeviceType } from '@composables/utils/useDeviceType';
import { useReviewerProfile } from '@composables/reviews/useReviewerProfile';
import { useModal } from '@composables/utils/useModal';
import { useProduct } from '@modules/product';
import { randomGenerator } from '@utils/commonUtils';
import { brandName } from 'config';
import ReviewerModal from './ReviewerModal';

const props = defineProps({
  userNickname: {
    type: String,
    required: true,
  },
  authorId: {
    type: String,
    required: true,
  },
  isSyndicated: {
    type: Boolean,
    default: false,
  },
  isEmployeeAuthor: {
    type: Boolean,
    default: false,
  },
});

const { productId } = useProduct();

const PROFILE_MODAL_ID = `reviewer-profile-modal-${randomGenerator()}`;
const { open } = useModal();
const { isMobile } = useDeviceType();
const TOGGLETIP_WIDTH_MOBILE = 200;
const TOGGLETIP_WIDTH_DESKTOP = 284;

const errorMessage = 'Something went wrong. We couldn\'t access the user\'s review profile.';

const toggleTipProfile = ref(null);
const hasValidAuthorId = computed(() => (props.authorId !== null && props.authorId !== undefined && props.authorId !== 'null'));

const {
  reviewerProfileData, isLoading, fetchProfileData, page,
} = useReviewerProfile();

onMounted(() => {
  watch(isLoading, () => {
    if (!isLoading.value) {
      const modalElement = document.querySelector(`#${PROFILE_MODAL_ID} > div`);
      if (modalElement) {
        if (isMobile.value) {
          modalElement.scrollTo(0, 0);
        } else {
          modalElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }
  });
});

const toggletipWidth = computed(() => (isMobile.value ? TOGGLETIP_WIDTH_MOBILE : TOGGLETIP_WIDTH_DESKTOP));

/** @param {{ event: object | unknown }} event */
function handleUsernameClick(event) {
  reviewerProfileData.value = {};
  fetchProfileData({
    id: props.authorId,
  });

  toggleTipProfile.value?.toggle(event);
  // BV track for stats - these run when user clicks on profile name
  trackEvent('profile', productId, 'summary_toggletip');
}

function openReviewerModal() {
  open(PROFILE_MODAL_ID);
  // BV track for stats - these run when user clicks on 'see user profile'
  trackEvent('profile', productId, 'full_modal');
}
</script>

<style lang="scss" scoped>
.medium,
.small {
  color: $white;
}

.loading-container {
  padding: rem-calc(10) 0;
}
</style>
